import { filter } from 'lodash';
import { DateRangePicker } from 'react-date-range';
import { endOfDay, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useRef, useState } from 'react';
import { Popover, popoverClasses, styled } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import Button from '../editors/mui/Button';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import './DatePicker.scss';

const muiStyles = {
  popover: {
    marginTop: '10px',
    [`.${popoverClasses.paper}`]: {
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      borderRadius: 4,
      zIndex: 5,
      transition: 'all 0.3s ease',
    },
  },
};

const StyledPopover = styled(Popover)(muiStyles.popover);

// Get user's timezone once
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDateWithoutTZ = (date) => {
  try {
    return formatInTimeZone(date, userTimeZone, 'MMM dd, yyyy h:mm a');
  } catch (error) {
    return format(date, 'MMM dd, yyyy h:mm a');
  }
};

const getTimezoneAbbr = (date) => {
  try {
    // Get timezone abbreviation from the browser's Intl API
    const tzAbbr = new Intl.DateTimeFormat('en', {
      timeZone: userTimeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName').value;
    return tzAbbr;
  } catch (error) {
    return formatInTimeZone(date, userTimeZone, 'zzz'); // fallback to date-fns-tz
  }
};

/**
 * normalizeDateRange intentionally returns an EXCLUSIVE upper bound
 * for the API query (e.g. selecting "Jul 30 - Jul 31" queries through Aug 1,
 * 12:00 AM) — but showing that boundary to the user would look like an extra
 * day was added.
 *
 * Humans read date ranges INCLUSIVELY, so the label must show
 * the LAST moment of the last selected day, not its first.
 *
 * A custom calendar drag/click lands `endDate` at local midnight of the last
 * selected day (its START, not its end) — unlike defaultRanges.js's date-fns
 * static ranges, which already end at 23:59:59.999.
 *
 * This only holds for whole-calendar-day selections. When `showPreciseTime`
 * is set (screens that allow picking an exact time, e.g. LogsNavigationPanel's
 * "Last 5 minutes"-style presets), `endDate` is already the intended precise
 * moment — rounding it to end-of-day would misrepresent the actual query range.
 */
export const formatDateRangeForHumanReadability = (startDate, endDate, showPreciseTime = false) => {
  const displayEndDate = showPreciseTime ? endDate : endOfDay(endDate);

  return `${formatDateWithoutTZ(startDate)} - ${formatDateWithoutTZ(displayEndDate)} (${getTimezoneAbbr(startDate)})`;
};

/**
 * Static-range matching is day-level only (isSameDay), so it's safe for
 * calendar-day-aligned presets (Today/Yesterday/...), but for rolling-window
 * presets (Last 5 minutes...Last 3 days) it can false-positive-match a manual
 * selection whose day span coincides with the preset's current span — silently
 * discarding the user's actual pick in favor of the preset's own reference time.
 * When showPreciseTime is set, skip the match entirely and trust the manual selection.
 */
export const resolveAppliedRange = (selectedRange, staticRanges, showPreciseTime = false) => {
  if (showPreciseTime) return selectedRange;

  const staticRangeMatch = filter(staticRanges, (range) => range.isSelected(selectedRange));
  return staticRangeMatch.length ? staticRangeMatch[0].range() : selectedRange;
};

const DatePicker = (props) => {
  const {
    onSelect,
    data,
    staticRanges,
    isLoading,
    dateFormat,
    extraProps,
    showPrefix = false,
    showResetButton = false,
    popoverClassName = '',
  } = props;

  const prefix = showPrefix ? (data?.label ? data.label : null) : null;
  const showPreciseTimeLabel = Boolean(extraProps?.showTimePicker);
  const label = formatDateRangeForHumanReadability(
    data.startDate,
    data.endDate,
    showPreciseTimeLabel,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState([data]);
  const _openCalendar = useRef();

  const _handleChange = (value) => {
    setIsOpen(false);
    onSelect(value);
  };

  const ResetButton = () => (
    <div className="reset-button-container">
      <button
        className="reset-button"
        onClick={() => {
          setState([data]);
          _handleChange(data);
        }}
      >
        Reset
      </button>
    </div>
  );

  const ApplyButton = () => (
    <div className="apply-button-container">
      <Button
        variant="contained"
        buttonTheme="primary"
        onClick={() => {
          _handleChange(resolveAppliedRange(state[0], staticRanges, showPreciseTimeLabel));
        }}
      >
        Apply
      </Button>
    </div>
  );

  const onChangeDateRangePicker = (item) => {
    if (item.selection.key === 'ranges') {
      _handleChange({ ...item.selection, key: 'selection' });
    } else {
      delete item.selection.label;
    }
    setState([{ ...item.selection, key: 'selection' }]);
  };

  return (
    <div className="datepicker-container">
      <label
        className="label"
        onClick={() => {
          if (!isLoading) {
            setIsOpen(!isOpen);
          }
        }}
        ref={_openCalendar}
      >
        {label}

        {prefix ? (
          <div className="prefix">
            <span>{prefix}</span>
          </div>
        ) : null}

        {isOpen ? <KeyboardArrowUp fontSize="large" /> : <KeyboardArrowDown fontSize="large" />}
      </label>
      <StyledPopover
        open={isOpen}
        anchorEl={_openCalendar?.current}
        onClose={() => {
          setIsOpen(false);
          // resetting data, if closed by popover
          setState([data]);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={popoverClassName}
      >
        <div className="date-range-calendar">
          <DateRangePicker
            className="date-range-calendar-wrapper"
            showTimePicker={true}
            onChange={onChangeDateRangePicker}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            maxDate={new Date()}
            months={2}
            ranges={state}
            direction="horizontal"
            staticRanges={staticRanges}
            inputRanges={[]}
            showMonthAndYearPickers={true}
            showDateDisplay={true} // hides dates
            retainEndDateOnFirstSelection={true}
            footerContent={
              <div className="calendar-footer-wrapper">
                <div className="timezone-info">
                  All times are shown in your local timezone ({getTimezoneAbbr(new Date())})
                </div>
                <div className="buttons-container">
                  {showResetButton && <ResetButton />}
                  <ApplyButton />
                </div>
              </div>
            }
            editableDateInputs={true}
            dateDisplayFormat={dateFormat} // Format for the calendar input
            dragSelectionEnabled={true}
            {...extraProps}
          />
        </div>
      </StyledPopover>
    </div>
  );
};

export default React.memo(DatePicker);
